import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import statsRosette from '@propertypal/shared/src/resources/icons/property/stats-rosette.svg?url';
import { trackGaLink } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { getFormattedPrice } from '@propertypal/shared/src/utils/property';
import { formatPropertyStats } from '@propertypal/shared/src/utils/property/chartUtils';
import Link from 'next/link';
import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import LineChart from '../charts/LineChart';
import { Box } from '../layout';
import { MAX_WIDTH } from '../layout/PageWrapper.style';
import useMediaQuery from '../media-query/useMediaQuery';
import { MidHeading, SmallHeading, SubHeading, Text } from '../typography';
import PromoteProperty from './PromoteProperty';
import { Container, RankingContainer, RosetteContainer, RosetteText, Wrapper } from './PropertyStats.style';

interface Props {
  property: Property;
  showPromoteText?: boolean;
}

const trackingLabel = 'Stats Graph';

const PropertyStats: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const { property } = props;
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const windowSize = useWindowSize();

  if (windowSize.width === undefined) return null;

  // If no property stats/stats history should state that stats should be available tomorrow
  if (!property.stats || (props.property.stats && !props.property.stats.history))
    return (
      <Wrapper data-testid="no-property-stats">
        {!isMobile && <SubHeading mt={40}>Property Statistics</SubHeading>}

        <MidHeading mt={30} fontWeight="normal" mb={30}>
          Property statistics not yet available, please return tomorrow to check.
        </MidHeading>

        {props.showPromoteText && <PromoteProperty property={property} label={trackingLabel} align="left" />}
      </Wrapper>
    );

  const chartWidth = isMobile ? windowSize.width - 50 : Math.min(MAX_WIDTH - 410, windowSize.width - 420);
  const chartHeight = isMobile ? 350 : 450;
  const from = getFormattedPrice(property.stats?.ranking?.range?.from, '£');
  const to = getFormattedPrice(property.stats?.ranking?.range?.to, '£');
  const lineData = formatPropertyStats(property, isMobile ? 7 : 21);
  const totalDays = lineData[0].data.reduce((prev, history) => prev + (!history.hidden ? 1 : 0), 0);

  return (
    <Wrapper data-testid="property-stats">
      {!isMobile && (
        <SubHeading mt={40} data-testid="property-stats-title">
          Property Statistics
        </SubHeading>
      )}

      <Container>
        {property.stats?.history && (
          <div>
            <SmallHeading fontWeight="normal" mt={30} fontSize={24}>
              Listing Views
            </SmallHeading>
            <Text fontSize={14}>(in the last {totalDays} days)</Text>

            <LineChart
              bubbleTextFormatter={(value, boosted) =>
                boosted
                  ? `Your property was boosted back up to the top of the search results resulting in ${value} views`
                  : `${value} Views`
              }
              width={chartWidth}
              height={chartHeight}
              lines={lineData}
              dottedPoints
              includeChartKeys
            />
          </div>
        )}

        {(property.stats?.totalViews || property.stats?.ranking) && (
          <RankingContainer>
            <SmallHeading fontWeight="normal" mt={30} fontSize={24} align="center">
              Total Views
            </SmallHeading>
            <Text fontSize={14} align="center">
              (since advertised)
            </Text>

            <SubHeading
              color={theme.primary}
              fontSize={54}
              fontWeight="normal"
              align="center"
              data-testid="property-total-views"
            >
              {getFormattedPrice(property.stats.totalViews, '')}
            </SubHeading>

            {property.stats.ranking && property.stats.ranking.popularity !== null && (
              <>
                <RosetteContainer>
                  <img src={statsRosette.src} width={statsRosette.width} height={statsRosette.height} alt="rosette" />
                  <RosetteText>{property.stats.ranking.popularity}</RosetteText>
                </RosetteContainer>

                <Text align="center" autoMargin fontSize={24} maxWidth={220} mt={10} mb={10}>
                  <span style={{ color: theme.primary }}>{property.stats.ranking.popularity}</span> most popular ranking
                  today in <span style={{ color: theme.primary }}>{property.stats.ranking.term}</span>
                </Text>

                {property.stats.ranking.range && (
                  <Text align="center" data-testid="rankingRangeText">
                    ({from}
                    {!property.stats.ranking.range.max ? ` - ${to}` : ''}
                    {property.stats.ranking.range.max ? '+' : ''})
                  </Text>
                )}

                {props.showPromoteText && (
                  <Link href={`/promote/premier?propertyId=${props.property.pathId}`} prefetch={false}>
                    <Button
                      onClick={trackGaLink('premium_click', 'Get More Views', 'PDP')}
                      containerStyle={{ marginTop: 30, width: 280 }}
                    >
                      Get more views
                    </Button>
                  </Link>
                )}
              </>
            )}
          </RankingContainer>
        )}
      </Container>
      {props.showPromoteText && (
        <Box mt={30}>
          <PromoteProperty property={property} label={trackingLabel} />
        </Box>
      )}
    </Wrapper>
  );
};

export default React.memo(PropertyStats);
